import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PssInventoryItem, PssItemDesign, PssCharacterDesign, PssCrewMember } from '../../../services/pss-auth.service';
import { CrewFlyoutComponent } from '../crew-flyout/crew-flyout.component';
import { spriteIdOrEmpty } from '../pss-display-utils';
import { PssSpriteComponent } from '../../pss-sprite/pss-sprite.component';

@Component({
  selector: 'app-use-item-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, CrewFlyoutComponent, PssSpriteComponent],
  templateUrl: './use-item-modal.component.html',
  styleUrls: ['../dashboard.component.css']
})
export class UseItemModalComponent {
  @Input() item: PssInventoryItem | null = null;
  @Input() itemDesigns = new Map<string, PssItemDesign>();
  @Input() inventory: PssInventoryItem[] = [];

  @Input() count = 1;
  @Input() maxCount = 0;
  @Input() progress = 0;
  @Input() total = 0;
  @Input() running = false;
  @Input() done = false;
  @Input() error = '';
  @Input() retryMsg = '';

  @Input() results: { rewardDesignId: string; rewardQty: number }[] = [];
  @Input() groupedResults: { rewardDesignId: string; totalQty: number }[] = [];
  @Input() grouped = true;
  @Input() selectedReward: string | null = null;
  @Input() characterDesigns = new Map<string, PssCharacterDesign>();
  @Input() characters: PssCrewMember[] = [];
  @Input() supportUrl = 'https://buymeacoffee.com/lordtrist';

  @Input() showFireOptions = false;
  @Input() fireCommon = false;
  @Input() fireElite = false;
  @Input() firedCharacterIds: ReadonlySet<string> = new Set<string>();

  selectedCharacter: PssCrewMember | null = null;

  private static readonly CREW_STAT_KEY_TO_CHARACTER_FIELD: Partial<Record<keyof PssCrewMember, keyof PssCharacterDesign>> = {
    weaponImp: 'finalWeapon',
    attackImp: 'finalAttack',
    scienceImp: 'finalScience',
    engineImp: 'finalEngine',
    pilotImp: 'finalPilot',
    repairImp: 'finalRepair',
    abilityImp: 'specialAbilityArg',
    hpImp: 'finalHp',
  };

  @Output() close = new EventEmitter<void>();
  @Output() viewCharacter = new EventEmitter<PssCrewMember>();
  @Output() start = new EventEmitter<void>();
  @Output() countChange = new EventEmitter<number>();
  @Output() groupedChange = new EventEmitter<boolean>();
  @Output() selectedRewardChange = new EventEmitter<string | null>();
  @Output() fireCommonChange = new EventEmitter<boolean>();
  @Output() fireEliteChange = new EventEmitter<boolean>();

  isFired(c: PssCrewMember): boolean {
    return this.firedCharacterIds.has(c.characterId);
  }

  get fireHint(): string {
    if (this.fireCommon && this.fireElite) return 'Common and Elite recruits will be permanently deleted as they are drawn.';
    if (this.fireCommon) return 'Common recruits will be permanently deleted as they are drawn.';
    if (this.fireElite) return 'Elite recruits will be permanently deleted as they are drawn.';
    return '';
  }

  itemName(designId: string): string {
    return this.itemDesigns.get(designId)?.name ?? `#${designId}`;
  }

  itemSpriteId(designId: string): string {
    return spriteIdOrEmpty(this.itemDesigns.get(designId)?.logoSpriteId);
  }

  itemRarity(designId: string): string {
    return this.itemDesigns.get(designId)?.rarity ?? '';
  }

  characterSpriteId(c: PssCrewMember): string {
    return spriteIdOrEmpty(this.characterDesigns.get(c.designId)?.profileSpriteId);
  }

  characterName(c: PssCrewMember): string {
    return c.customName || this.characterDesigns.get(c.designId)?.name || `#${c.designId}`;
  }

  crewRarity(c: PssCrewMember): string {
    return this.characterDesigns.get(c.designId)?.rarity || 'common';
  }

  crewBaseStat(c: PssCrewMember, stat: keyof PssCrewMember): number {
    const designField = UseItemModalComponent.CREW_STAT_KEY_TO_CHARACTER_FIELD[stat];
    if (!designField) return 0;
    const charDesign = this.characterDesigns.get(c.designId);
    if (!charDesign) return 0;
    return parseFloat((charDesign[designField] as string) || '0') || 0;
  }

  crewTrainingPct(c: PssCrewMember, stat: keyof PssCrewMember): number {
    return parseInt((c[stat] as string) || '0', 10) || 0;
  }

  crewTotalStat(c: PssCrewMember, stat: keyof PssCrewMember): number {
    const base = this.crewBaseStat(c, stat);
    const training = this.crewTrainingPct(c, stat);
    return Number.isFinite(base) ? base * (1 + training / 100) : 0;
  }

  openCharacterFlyout(c: PssCrewMember): void {
    this.selectedCharacter = this.selectedCharacter === c ? null : c;
    this.viewCharacter.emit(c);
  }

  closeCharacterFlyout(): void {
    this.selectedCharacter = null;
  }

  setCount(next: number): void {
    this.countChange.emit(Math.max(1, Math.min(next, this.maxCount || 1)));
  }

  toggleReward(id: string): void {
    this.selectedRewardChange.emit(this.selectedReward === id ? null : id);
  }
}
